/**
 * BuildingBlocksSection — «Building blocks»: the operator's library-example
 * selection for a strategy generation.
 *
 * Two renderings of ONE selection model (`BlockLibrarySelection[]`):
 * - `variant="list"` (default) — the collapsible list + preview pane
 *   (approved mock: strategy-form-building-blocks-default.html). Collapsed by
 *   default; the list/preview mount ONLY while expanded — SPEC-021: SAccordion
 *   keeps collapsed content mounted and `useScreenScale` early-returns on
 *   zero-size containers, so pre-mounted thumbnails stay invisible forever.
 * - `variant="table"` — create-wizard step 8 (W12 Part B, Task B5): the
 *   wizard's ONE `SelectionTable` recipe (checkbox · Name + variable fit ·
 *   Source · Type · Insert mode · ⓘ — five columns that fit the 800px content
 *   column, Task B9d). The ⓘ swaps `ExampleDetailsView` INTO the step — never a
 *   second dialog (designer invariant 18).
 *
 * Selection model (shared by both variants — `useLibrarySelectionModel`): each
 * checked example carries a mode (Include as-is | Adapt | Pattern) — DEFAULT
 * Adapt (user decision: «блок = шаблон», invariant 9). Caps mirror the
 * SERVER-enforced LIBRARY_SELECTION_CAPS honestly: at the materialized cap a
 * new selection enters as PATTERN, and a control disables only when NOTHING is
 * possible, with the reason named (invariant 14).
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  assessLibraryExampleRenderability,
  computeContractFit,
  LIBRARY_SELECTION_CAPS,
  type BlockLibraryExampleDoc,
  type BlockLibrarySelection,
} from '@modules/shared-types';
import type { SDataTableColumnDef } from '@modules/ui-components/dialogs/SDataTable';
import { STRATEGY_LIST } from '@test-kit/selectors/strategy-list';
import { ExampleScreensFilmstrip } from './ExampleScreensFilmstrip';
import { ExampleDetailsView } from './ExampleDetailsView';
import { SelectionTable, type SelectionRow } from './strategyWizard/SelectionTable';
import { examplePreviewScreens } from '../utils/exampleEnvelope';
import { fitLine, fitSummary, type ContractFitSummary } from '../utils/contractFitText';
import { warningToneText } from '../utils/warningTone';

export interface BuildingBlocksSectionProps {
  projectExamples: BlockLibraryExampleDoc[];
  systemExamples: BlockLibraryExampleDoc[];
  /** Current form variable map — feeds the advisory contract-fit lines. */
  strategyVariables: Record<string, unknown>;
  value: BlockLibrarySelection[];
  onChange: (selections: BlockLibrarySelection[]) => void;
  disabled?: boolean;
  /** Library subscriptions still loading (AC-P3-005). */
  loading?: boolean;
  /** Library read error, if any (AC-P3-005). */
  error?: string | null;
  /** `'list'` = the collapsible section (default); `'table'` = create-wizard step 8. */
  variant?: 'list' | 'table';
  /** Names this strategy in the details view's contract-fit header. */
  strategyName?: string;
}

interface SectionExample {
  doc: BlockLibraryExampleDoc;
  source: 'project' | 'system';
}

const MODES: Array<BlockLibrarySelection['mode']> = ['include', 'adapt', 'pattern'];
const MODE_LABELS: Record<BlockLibrarySelection['mode'], string> = {
  include: 'Include as-is',
  adapt: 'Adapt',
  pattern: 'Pattern',
};

const selectionKey = (source: string, exampleId: string) => `${source}:${exampleId}`;
/**
 * The identity of a library example INSIDE this section — its tier-aware key.
 * A document id alone is not one: a project example and a system example may
 * carry the SAME id (a saved copy without `systemExampleId`), and addressing
 * either by the bare id made the project row unreachable (D-LIVE2-1). Every
 * lookup, row id and testid below goes through this.
 */
const entryKey = (entry: SectionExample) => selectionKey(entry.source, entry.doc.id);

// examplePreviewScreens/fitLine live in ../utils — shared with ExampleDetailsView.

/**
 * The rules BOTH variants obey: which examples exist, what is selected, what
 * the caps still allow, and every write. Kept in one place so the list and the
 * table can never disagree about a cap or a fit.
 */
interface LibrarySelectionModel {
  entries: SectionExample[];
  selectionOf: (entry: SectionExample) => BlockLibrarySelection | undefined;
  materializedCount: number;
  patternCount: number;
  materializedFull: boolean;
  patternFull: boolean;
  capsCounter: string;
  renderabilityOf: (entry: SectionExample) => { renderable: boolean; reason?: string };
  /** Why this example cannot ENTER the selection (undefined = it can). */
  blockedReason: (entry: SectionExample) => string | undefined;
  /** Toggle one example (add at the honest mode / remove). */
  toggleSelection: (entry: SectionExample) => void;
  /** Set (or, for an unselected example, ADD at) one insert mode. */
  chooseMode: (entry: SectionExample, mode: BlockLibrarySelection['mode'] | null) => void;
  /** True when this mode cannot be chosen right now (caps / read-only). */
  modeDisabled: (entry: SectionExample, mode: BlockLibrarySelection['mode']) => boolean;
  /** Replace the selection among the VISIBLE entries with these entry KEYS. */
  selectEntryKeys: (keys: string[]) => void;
}

function useLibrarySelectionModel({
  projectExamples,
  systemExamples,
  value,
  onChange,
  disabled,
}: BuildingBlocksSectionProps): LibrarySelectionModel {
  const entries = useMemo<SectionExample[]>(() => {
    const systemIds = new Set(systemExamples.map((doc) => doc.id));
    return [
      ...projectExamples
        .filter((doc) => doc.enabled !== false)
        // One row per example — imported copies are represented by their system row.
        .filter((doc) => !(doc.systemExampleId && systemIds.has(doc.systemExampleId)))
        .map((doc) => ({ doc, source: 'project' as const })),
      ...systemExamples.filter((doc) => doc.enabled !== false).map((doc) => ({ doc, source: 'system' as const })),
    ];
  }, [projectExamples, systemExamples]);

  const selectionByKey = useMemo(() => new Map(
    value.map((selection) => [selectionKey(selection.source, selection.exampleId), selection]),
  ), [value]);

  const materializedCount = value.filter((selection) => selection.mode !== 'pattern').length;
  const patternCount = value.length - materializedCount;
  const materializedFull = materializedCount >= LIBRARY_SELECTION_CAPS.materialized;
  const patternFull = patternCount >= LIBRARY_SELECTION_CAPS.pattern;

  const selectionOf = useCallback(
    (entry: SectionExample) => selectionByKey.get(entryKey(entry)),
    [selectionByKey],
  );

  const renderabilityOf = useCallback(
    (entry: SectionExample) => assessLibraryExampleRenderability(entry.doc),
    [],
  );

  const blockedReason = useCallback((entry: SectionExample): string | undefined => {
    const renderability = assessLibraryExampleRenderability(entry.doc);
    // AC-P3-055: a broken envelope renders INERT with the reason named — it
    // never joins a selection or counts against caps.
    if (!renderability.renderable) {
      return `This example cannot be inserted: ${renderability.reason}`;
    }
    // #P3-3: otherwise only BOTH caps exhausted blocks a new selection (a new
    // one can still enter as pattern while pattern slots remain).
    if (materializedFull && patternFull) {
      return `Selection caps reached (${LIBRARY_SELECTION_CAPS.materialized} placed + ${LIBRARY_SELECTION_CAPS.pattern} pattern) — deselect an example to add another.`;
    }
    return undefined;
  }, [materializedFull, patternFull]);

  /**
   * The ONE writer. `keys` are the ENTRY KEYS (`<source>:<doc.id>`) that should
   * be selected among the VISIBLE entries; selections of examples this view does
   * not list (a stale id, another source) travel untouched. New keys enter at
   * `adapt` while materialized slots remain, then at `pattern` (invariant 14) —
   * and stop when neither cap has room, which the disabled checkbox already
   * explains.
   */
  const selectEntryKeys = useCallback((keys: string[]) => {
    const entryByKey = new Map(entries.map((entry) => [entryKey(entry), entry]));
    const visibleKeys = new Set(entries.map(entryKey));
    const wantedKeys = new Set(keys.filter((key) => entryByKey.has(key)));

    const kept = value.filter((selection) => {
      const key = selectionKey(selection.source, selection.exampleId);
      return !visibleKeys.has(key) || wantedKeys.has(key);
    });
    let materialized = kept.filter((selection) => selection.mode !== 'pattern').length;
    let pattern = kept.length - materialized;
    const keptKeys = new Set(kept.map((s) => selectionKey(s.source, s.exampleId)));

    const added: BlockLibrarySelection[] = [];
    for (const key of keys) {
      const entry = entryByKey.get(key);
      if (!entry) continue;
      if (keptKeys.has(key)) continue;
      if (!assessLibraryExampleRenderability(entry.doc).renderable) continue;
      if (materialized < LIBRARY_SELECTION_CAPS.materialized) {
        added.push({ source: entry.source, exampleId: entry.doc.id, mode: 'adapt' });
        materialized += 1;
      } else if (pattern < LIBRARY_SELECTION_CAPS.pattern) {
        added.push({ source: entry.source, exampleId: entry.doc.id, mode: 'pattern' });
        pattern += 1;
      }
    }
    onChange([...kept, ...added]);
  }, [entries, onChange, value]);

  const toggleSelection = useCallback((entry: SectionExample) => {
    if (disabled) return;
    const visibleSelectedKeys = entries
      .map(entryKey)
      .filter((key) => selectionByKey.has(key));
    const key = entryKey(entry);
    const selected = visibleSelectedKeys.includes(key);
    selectEntryKeys(selected
      ? visibleSelectedKeys.filter((candidate) => candidate !== key)
      : [...visibleSelectedKeys, key]);
  }, [disabled, entries, selectEntryKeys, selectionByKey]);

  const chooseMode = useCallback((
    entry: SectionExample,
    mode: BlockLibrarySelection['mode'] | null,
  ) => {
    if (!mode || disabled) return;
    const key = entryKey(entry);
    if (!selectionByKey.has(key)) {
      // From the details view: choosing a mode on an unselected example ADDS it
      // at that mode — what the control shows is what the request carries. The
      // ENTRY guard is the same one `selectEntryKeys` applies: a broken
      // envelope (AC-P3-055) or an exhausted pair of caps never joins the
      // selection, whatever control is used.
      if (blockedReason(entry)) return;
      onChange([...value, { source: entry.source, exampleId: entry.doc.id, mode }]);
      return;
    }
    onChange(value.map((selection) => (
      selectionKey(selection.source, selection.exampleId) === key ? { ...selection, mode } : selection
    )));
  }, [blockedReason, disabled, onChange, selectionByKey, value]);

  const modeDisabled = useCallback((
    entry: SectionExample,
    mode: BlockLibrarySelection['mode'],
  ): boolean => {
    if (disabled) return true;
    const current = selectionOf(entry)?.mode;
    // Nothing the example cannot ENTER the selection for is choosable — the
    // control names the reason instead of writing a selection the model
    // refuses (AC-P3-055 / invariant 14).
    if (current === undefined && blockedReason(entry)) return true;
    // Moving INTO pattern needs a pattern slot…
    if (mode === 'pattern') return current !== 'pattern' && patternFull;
    // …and entering a materialized mode (from pattern, or from nothing at all)
    // needs a materialized slot.
    const alreadyMaterialized = current === 'include' || current === 'adapt';
    return !alreadyMaterialized && materializedFull;
  }, [blockedReason, disabled, materializedFull, patternFull, selectionOf]);

  return {
    entries,
    selectionOf,
    materializedCount,
    patternCount,
    materializedFull,
    patternFull,
    capsCounter: `${materializedCount} of ${LIBRARY_SELECTION_CAPS.materialized} placed · ${patternCount} of ${LIBRARY_SELECTION_CAPS.pattern} pattern`,
    renderabilityOf,
    blockedReason,
    toggleSelection,
    chooseMode,
    modeDisabled,
    selectEntryKeys,
  };
}

export const BuildingBlocksSection: React.FC<BuildingBlocksSectionProps> = (props) => {
  const model = useLibrarySelectionModel(props);
  return props.variant === 'table'
    ? <BuildingBlocksTable {...props} model={model} />
    : <BuildingBlocksList {...props} model={model} />;
};

BuildingBlocksSection.displayName = 'BuildingBlocksSection';

// ───────────────────────────── table variant ──────────────────────────────

interface LibraryRow extends SelectionRow {
  entry: SectionExample;
  sourceLabel: 'Project' | 'System';
  blockType: string;
  fit: ContractFitSummary;
  mode: BlockLibrarySelection['mode'] | null;
}

type VariantProps = BuildingBlocksSectionProps & { model: LibrarySelectionModel };

/**
 * The approved canvas v3.3 sentence under the library table, verbatim from
 * `.design/create-strategy-wizard/tools/gen-wizard-v3.mjs` — it is the only
 * place the ⓘ and the two non-default insert modes are named, and the REVIEW
 * found its absence made a shipped feature undiscoverable (Task B9d).
 */
export const LIBRARY_TABLE_FOOTER =
  'A selected example is adapted by the AI by default; open ⓘ to see its screens,'
  + ' the rationale and the variable fit, or to choose Include as-is / Pattern.';

const BuildingBlocksTable: React.FC<VariantProps> = ({
  model,
  strategyVariables,
  strategyName,
  disabled,
  loading,
  error,
}) => {
  /** The ENTRY KEY of the example whose details are swapped in (never a bare id). */
  const [detailsId, setDetailsId] = useState<string | null>(null);

  const rows = useMemo<LibraryRow[]>(() => model.entries.map((entry) => ({
    // The row identity is the TIER-AWARE key (D-LIVE2-1): `selectedIds` /
    // `onSelectedIdsChange` round-trip keys, and the table's own row testid
    // (`…-row-<source>:<id>`) tells the two tiers' rows apart (F-LIVE2-5).
    id: entryKey(entry),
    name: entry.doc.name,
    entry,
    sourceLabel: entry.source === 'system' ? 'System' : 'Project',
    blockType: entry.doc.scopeMetadata.blockType,
    fit: fitSummary(computeContractFit(entry.doc, strategyVariables as never)),
    mode: model.selectionOf(entry)?.mode ?? null,
  })), [model.entries, model.selectionOf, strategyVariables]);

  const selectedIds = rows.filter((row) => row.mode !== null).map((row) => row.id);
  const detailsEntry = model.entries.find((entry) => entryKey(entry) === detailsId) ?? null;

  // Task B9d must-fix 2: the six columns measured ~892px inside the 800px
  // content column, so «Insert mode» was clipped and the ⓘ — the ONLY way into
  // the example details (invariant 25 / D16) — was off-canvas at every
  // breakpoint. «Variable fit» is one short phrase, so it moves under the name
  // as a second line and the remaining five columns fit with room to spare.
  const columns = useMemo<SDataTableColumnDef<LibraryRow>[]>(() => [
    {
      accessorKey: 'name',
      header: 'Name',
      size: 240,
      Cell: ({ row }) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.original.name}</Typography>
          {row.original.fit.fits ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              fits
            </Typography>
          ) : (
            // Invariant 10: an honest amber lock that NAMES what is uncovered.
            <Stack direction="row" spacing={0.5} alignItems="flex-start">
              <LockOutlinedIcon sx={{ fontSize: 13, mt: '2px', color: warningToneText }} />
              <Typography variant="caption" sx={{ color: warningToneText }}>
                {row.original.fit.label}
              </Typography>
            </Stack>
          )}
        </Box>
      ),
    },
    {
      accessorKey: 'sourceLabel',
      header: 'Source',
      size: 72,
      Cell: ({ row }) => (
        <Chip
          size="small"
          variant="outlined"
          label={row.original.sourceLabel}
          color={row.original.sourceLabel === 'System' ? 'primary' : 'default'}
          // Invariant 15: primary.main text fails AA on this dialog's grey paper.
          sx={(theme) => ({
            height: 20,
            ...(row.original.sourceLabel === 'System' && theme.palette.mode === 'light'
              ? { color: theme.palette.primary.dark, borderColor: theme.palette.primary.dark }
              : {}),
          })}
        />
      ),
    },
    {
      accessorKey: 'blockType',
      header: 'Type',
      size: 100,
      Cell: ({ row }) => (
        <Typography variant="body2" color="text.secondary">{row.original.blockType}</Typography>
      ),
    },
    {
      accessorKey: 'mode',
      header: 'Insert mode',
      size: 120,
      Cell: ({ row }) => (
        <Typography variant="body2" color={row.original.mode ? 'text.primary' : 'text.secondary'}>
          {row.original.mode ? MODE_LABELS[row.original.mode] : '—'}
        </Typography>
      ),
    },
    {
      // Always the LAST visible column and never wider than the glyph it
      // carries: the ⓘ is the only door to the example details.
      id: 'details',
      header: '',
      size: 44,
      grow: false,
      enableColumnActions: false,
      Cell: ({ row }) => (
        <Tooltip title="Example details">
          <IconButton
            size="small"
            aria-label="Example details"
            data-testid={STRATEGY_LIST.wizardLibraryRowInfo(row.original.id)}
            onClick={() => setDetailsId(row.original.id)}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ], []);

  if (detailsEntry) {
    return (
      <ExampleDetailsPane
        entry={detailsEntry}
        model={model}
        strategyVariables={strategyVariables}
        strategyName={strategyName}
        disabled={disabled}
        onBack={() => setDetailsId(null)}
      />
    );
  }

  return (
    <SelectionTable<LibraryRow>
      rows={rows}
      selectedIds={selectedIds}
      onSelectedIdsChange={model.selectEntryKeys}
      columns={columns}
      disabled={disabled}
      rowSelectable={(row) => model.blockedReason(row.entry) === undefined}
      rowDisabledReason={(row) => model.blockedReason(row.entry)}
      loading={loading}
      error={error ?? null}
      searchPlaceholder="Search library examples"
      summary={`${selectedIds.length} of ${rows.length} selected · ${model.capsCounter}`}
      footer={LIBRARY_TABLE_FOOTER}
      emptyState={(
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          No examples yet — save a block as an example from the strategy canvas ⋮ menu.
        </Typography>
      )}
      aria-label="Block library examples"
      data-testid={STRATEGY_LIST.wizardLibraryTable}
    />
  );
};

/**
 * The example details, swapped INTO the step (invariant 18 — no second dialog).
 * `ExampleDetailsView` is the single shared content view; the insert mode and
 * the add/remove action are contained here, next to it.
 */
const ExampleDetailsPane: React.FC<{
  entry: SectionExample;
  model: LibrarySelectionModel;
  strategyVariables: Record<string, unknown>;
  strategyName?: string;
  disabled?: boolean;
  onBack: () => void;
}> = ({ entry, model, strategyVariables, strategyName, disabled, onBack }) => {
  const selection = model.selectionOf(entry);
  const fit = fitSummary(computeContractFit(entry.doc, strategyVariables as never));
  const renderability = model.renderabilityOf(entry);
  // Invariant 10: «Include as-is» is not on offer when the contract has to be
  // rebound — the row already names the variables that force the adaptation.
  const modes = fit.fits ? MODES : MODES.filter((mode) => mode !== 'include');
  const blockedReason = selection ? undefined : model.blockedReason(entry);

  return (
    <Stack spacing={2} data-testid={STRATEGY_LIST.wizardLibraryDetails}>
      <Button
        variant="text"
        size="small"
        data-testid={STRATEGY_LIST.wizardLibraryDetailsBack}
        onClick={onBack}
        sx={{ alignSelf: 'flex-start', color: 'primary.dark', textTransform: 'none' }}
      >
        ← Back to the examples
      </Button>

      <ExampleDetailsView
        example={entry.doc}
        source={entry.source}
        strategyVariables={strategyVariables}
        strategyName={strategyName}
      />

      <Stack spacing={0.75}>
        <Typography variant="overline" color="text.secondary">Insert mode</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          role="radiogroup"
          aria-label="Insert mode"
          value={selection?.mode ?? null}
          onChange={(_event, mode) => model.chooseMode(entry, mode as BlockLibrarySelection['mode'] | null)}
          // #P3-2: the mock fills the active segment with primary + contrast
          // text; MUI's default tint fails 3:1 state contrast.
          sx={{
            alignSelf: 'flex-start',
            '& .MuiToggleButton-root': {
              py: 0.25, px: 1.25, textTransform: 'none',
              color: 'text.primary',
              borderColor: 'text.secondary',
              '&:focus-visible': { outline: 2, outlineColor: 'primary.main', outlineOffset: -2 },
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { backgroundColor: 'primary.dark' },
                '&:focus-visible': { outlineColor: 'primary.contrastText' },
              },
            },
          }}
        >
          {modes.map((mode) => (
            <ToggleButton
              key={mode}
              value={mode}
              role="radio"
              aria-checked={selection?.mode === mode}
              disabled={model.modeDisabled(entry, mode)}
            >
              {MODE_LABELS[mode]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {blockedReason && (
          <Typography variant="caption" sx={{ color: warningToneText }}>
            {blockedReason}
          </Typography>
        )}
        {!fit.fits && (
          <Typography variant="caption" sx={{ color: warningToneText }}>
            «Include as-is» is unavailable: this strategy already defines{' '}
            {fit.needsAdapt.join(', ')} differently — the adaptation rebinds the example to it.
          </Typography>
        )}
      </Stack>

      <Box>
        <Button
          variant={selection ? 'outlined' : 'contained'}
          color={selection ? 'inherit' : 'primary'}
          size="small"
          disabled={Boolean(disabled) || !renderability.renderable || Boolean(blockedReason)}
          title={blockedReason}
          onClick={() => model.toggleSelection(entry)}
        >
          {selection ? 'Remove from the strategy' : 'Add to the strategy'}
        </Button>
      </Box>
    </Stack>
  );
};

// ───────────────────────────── list variant ───────────────────────────────

const BuildingBlocksList: React.FC<VariantProps> = ({
  model,
  strategyVariables,
  disabled,
  loading,
  error,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState('');
  /** The ENTRY KEY of the previewed example (never a bare id — D-LIVE2-1). */
  const [focusedId, setFocusedId] = useState<string | null>(null);

  const { entries } = model;

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return entries;
    return entries.filter(({ doc }) => [
      doc.name, doc.description, doc.scopeMetadata.blockType, ...(doc.tags ?? []),
    ]
      .filter(Boolean).join(' ').toLowerCase().includes(needle));
  }, [entries, query]);

  const focused = filtered.find((entry) => entryKey(entry) === focusedId) ?? null;
  const focusedFit = useMemo(() => (focused
    ? computeContractFit(focused.doc, strategyVariables as never)
    : []), [focused, strategyVariables]);

  const summary = model.materializedCount + model.patternCount === 0 ? 'optional' : model.capsCounter;

  return (
    <Box
      data-testid={STRATEGY_LIST.buildingBlocksSection}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
    >
      <Box
        data-testid={STRATEGY_LIST.buildingBlocksToggle}
        role="button"
        tabIndex={0}
        aria-label="Building blocks"
        aria-expanded={expanded}
        onClick={() => setExpanded((previous) => !previous)}
        // #P3-1: a keyboard user must reach the section — Enter/Space toggle.
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setExpanded((previous) => !previous);
          }
        }}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
          cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' },
          '&:focus-visible': { outline: 2, outlineColor: 'primary.main', outlineOffset: -2 },
        }}
      >
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          🧱 Building blocks
          <Typography component="span" variant="caption" color="text.secondary"> — {summary}</Typography>
        </Typography>
        <Typography
          data-testid={STRATEGY_LIST.buildingBlocksCapsCounter}
          variant="caption"
          color="text.secondary"
        >
          {model.capsCounter}
        </Typography>
        <IconButton size="small" tabIndex={-1} aria-hidden>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* SPEC-021: content mounts ONLY while expanded. */}
      <Collapse in={expanded} unmountOnExit mountOnEnter>
        <Box sx={{ display: 'flex', gap: 1.5, p: 1.5, pt: 0.5, minHeight: 220 }}>
          <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
            <TextField
              data-testid={STRATEGY_LIST.buildingBlocksSearch}
              size="small"
              placeholder="Search library examples"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                ),
              }}
            />
            <Stack spacing={0.75} sx={{ overflowY: 'auto', maxHeight: 300 }}>
              {/* AC-P3-005: loading / error / empty are DISTINCT states. */}
              {loading && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2, justifyContent: 'center' }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">Loading library…</Typography>
                </Stack>
              )}
              {!loading && error && (
                <Alert severity="error" sx={{ py: 0.25 }}>
                  Library could not be loaded: {error}
                </Alert>
              )}
              {!loading && !error && entries.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  No examples yet — save a block as an example from the strategy canvas ⋮ menu.
                </Typography>
              )}
              {!loading && !error && entries.length > 0 && filtered.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  No library examples match.
                </Typography>
              )}
              {filtered.map((entry) => {
                const selection = model.selectionOf(entry);
                const checked = Boolean(selection);
                const disabledReason = checked ? undefined : model.blockedReason(entry);
                const disableCheckbox = Boolean(disabled) || (!checked && Boolean(disabledReason));
                const key = entryKey(entry);
                return (
                  <Box
                    key={key}
                    data-testid={STRATEGY_LIST.buildingBlocksItem(key)}
                    onClick={() => setFocusedId(key)}
                    sx={{
                      border: 1,
                      borderColor: focusedId === key ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      p: 1,
                      display: 'flex',
                      gap: 0.75,
                      alignItems: 'flex-start',
                      bgcolor: checked ? 'action.selected' : undefined,
                      cursor: 'pointer',
                    }}
                  >
                    <Box
                      data-testid={STRATEGY_LIST.buildingBlocksSelect(key)}
                      onClick={(event) => event.stopPropagation()}
                      title={disabledReason}
                    >
                      <Checkbox
                        size="small"
                        checked={checked}
                        disabled={disableCheckbox}
                        onChange={() => model.toggleSelection(entry)}
                        inputProps={{
                          'aria-label': `Select ${entry.doc.name}`,
                          ...(disabledReason ? { 'aria-description': disabledReason } : {}),
                        }}
                        sx={{ p: 0.25 }}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" component="div" sx={{ fontWeight: 600 }} noWrap>
                        {entry.doc.name}{' '}
                        <Chip
                          label={entry.source === 'system' ? 'System' : 'My'}
                          size="small"
                          variant={entry.source === 'system' ? 'outlined' : 'filled'}
                          color={entry.source === 'system' ? 'primary' : 'default'}
                          // #P3-2 companion: this dialog's paper is grey (250,250,250)
                          // — primary.main text drops below AA there; darken in light.
                          sx={(theme) => ({
                            height: 16,
                            fontSize: 9,
                            ...(entry.source === 'system' && theme.palette.mode === 'light'
                              ? { color: theme.palette.primary.dark, borderColor: theme.palette.primary.dark }
                              : {}),
                          })}
                        />
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap component="div">
                        {entry.doc.screensCount} screens · {entry.doc.scopeMetadata.blockType} · contract{' '}
                        {entry.doc.contractSummary.inputs}→{entry.doc.contractSummary.outputs}
                      </Typography>
                      {checked && selection && (
                        <ToggleButtonGroup
                          size="small"
                          exclusive
                          value={selection.mode}
                          onChange={(_event, mode) => model.chooseMode(entry, mode)}
                          // #P3-2: the mock fills the active segment with primary +
                          // contrast text; MUI's default tint fails 3:1 state
                          // contrast and the 10px unselected labels miss AA.
                          sx={{
                            mt: 0.5,
                            '& .MuiToggleButton-root': {
                              py: 0, px: 1, fontSize: 11, textTransform: 'none',
                              color: 'text.primary',
                              borderColor: 'text.secondary',
                              '&:focus-visible': { outline: 2, outlineColor: 'primary.main', outlineOffset: -2 },
                              '&.Mui-selected': {
                                backgroundColor: 'primary.main',
                                color: 'primary.contrastText',
                                '&:hover': { backgroundColor: 'primary.dark' },
                                // #P3-2b: an inset primary ring vanishes on the
                                // primary-filled segment — use the contrast color.
                                '&:focus-visible': { outlineColor: 'primary.contrastText' },
                              },
                            },
                          }}
                        >
                          {MODES.map((mode) => (
                            <ToggleButton
                              key={mode}
                              value={mode}
                              data-testid={STRATEGY_LIST.buildingBlocksMode(key, mode)}
                              disabled={model.modeDisabled(entry, mode)}
                            >
                              {MODE_LABELS[mode]}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Stack>

          {focused && (
            <Box
              data-testid={STRATEGY_LIST.buildingBlocksPreview}
              sx={{ flex: 1, minWidth: 0, borderLeft: 1, borderColor: 'divider', pl: 1.5 }}
            >
              <Typography variant="subtitle2">{focused.doc.name}</Typography>
              {focused.doc.description && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                  {focused.doc.description}
                </Typography>
              )}
              {(focused.doc.tags?.length ?? 0) > 0 && (
                <Stack direction="row" spacing={0.5} sx={{ mb: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
                  {focused.doc.tags!.map((tag) => (
                    <Chip key={tag} label={tag} size="small" sx={{ height: 16, fontSize: 9 }} />
                  ))}
                </Stack>
              )}
              <Typography variant="overline" color="text.secondary" component="div">
                Contract fit — this strategy
              </Typography>
              <Stack spacing={0.25}>
                {focusedFit.length === 0 && (
                  <Typography variant="caption" color="text.secondary">No contract variables — clean drop-in.</Typography>
                )}
                {focusedFit.map((entry) => (
                  <Typography key={`${entry.kind}-${entry.exampleVarId}`} variant="caption" component="div">
                    {fitLine(entry)}
                  </Typography>
                ))}
              </Stack>
              <Typography variant="overline" color="text.secondary" component="div" sx={{ mt: 1 }}>
                Screens ({examplePreviewScreens(focused.doc).length})
              </Typography>
              {/* AC-P3-009 closed: REAL static mini-renders (V2ScreenView via
                  the shared filmstrip) replace the schematic skeletons. */}
              <ExampleScreensFilmstrip
                screens={examplePreviewScreens(focused.doc)}
                size="small"
                data-testid="building-blocks-preview-screens"
              />
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                🧩 <b>Include as-is</b>: placed verbatim, contract adopted. <b>Adapt</b>: structure
                &amp; layout kept — the AI rebinds the contract to this strategy&apos;s variables and
                rewrites copy to your product and tone. <b>Pattern</b>: style/structure reference only.
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
